import { useMemo, useState } from 'react';
import Editor from '@monaco-editor/react';
import { Alert, App, Button, Empty, Spin, Table, Tabs, Tag, Typography } from 'antd';
import { CheckCircleFilled, CloseCircleFilled, SaveOutlined } from '@ant-design/icons';
import type { HistoryEntry } from '@core/entities/History';
import { useThemeStore } from '@presentation/stores/themeStore';
import { useTestResultsStore } from '@presentation/stores/testResultsStore';
import { useCreateRequestExample } from '@presentation/hooks/useRequestExamples';
import { NamePromptModal } from '@presentation/components/workbench/NamePromptModal';

interface ResponseViewerProps {
  entry: HistoryEntry | undefined;
  isSending: boolean;
  /** When given, adds a "Tests" tab reading results for this workbench tab from testResultsStore. */
  tabKey?: string;
  /** When given (a saved request), adds a "Save as Example" button to the response header. */
  requestId?: string | null;
}

const statusColor = (statusCode: number | null): string => {
  if (statusCode === null) return 'default';
  if (statusCode < 300) return 'success';
  if (statusCode < 400) return 'processing';
  if (statusCode < 500) return 'warning';
  return 'error';
};

const formatBytes = (bytes: number | null): string => {
  if (bytes === null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const detectLanguage = (headers: Record<string, string[]> | null): string => {
  const contentType = headers?.['Content-Type']?.[0] ?? headers?.['content-type']?.[0] ?? '';
  if (contentType.includes('json')) return 'json';
  if (contentType.includes('xml')) return 'xml';
  if (contentType.includes('html')) return 'html';
  return 'plaintext';
};

const tryFormat = (body: string, language: string): string => {
  if (language !== 'json') return body;
  try {
    return JSON.stringify(JSON.parse(body), null, 2);
  } catch {
    return body;
  }
};

export const ResponseViewer = ({ entry, isSending, tabKey, requestId }: ResponseViewerProps) => {
  const { message } = App.useApp();
  const themeMode = useThemeStore((state) => state.mode);
  const [tab, setTab] = useState('pretty');
  const [savingExample, setSavingExample] = useState(false);
  const testState = useTestResultsStore((state) => (tabKey ? state.results[tabKey] : undefined));
  const createExample = useCreateRequestExample(requestId ?? '');

  const language = useMemo(() => detectLanguage(entry?.response?.headers ?? null), [entry]);
  const prettyBody = useMemo(
    () => (entry?.response?.body ? tryFormat(entry.response.body, language) : ''),
    [entry, language],
  );

  if (isSending) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spin description="Sending..." />
      </div>
    );
  }

  if (!entry) {
    return (
      <div className="flex h-full items-center justify-center">
        <Empty description="Send a request to see the response here" />
      </div>
    );
  }

  if (entry.errorMessage) {
    return (
      <div className="p-4">
        <Alert type="error" showIcon title="Request failed" description={entry.errorMessage} />
      </div>
    );
  }

  const headerRows = Object.entries(entry.response?.headers ?? {}).map(([name, values]) => ({
    key: name,
    name,
    value: values.join(', '),
  }));

  const cookieRows = (entry.response?.cookies ?? []).map((cookie, index) => ({
    key: index,
    name: String(cookie.name ?? ''),
    value: String(cookie.value ?? ''),
    domain: String(cookie.domain ?? ''),
    path: String(cookie.path ?? ''),
  }));

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-4 border-b border-black/10 px-3 py-2 text-sm dark:border-white/10">
        <Tag color={statusColor(entry.statusCode)}>
          {entry.statusCode ?? '—'} {entry.response?.statusText ?? ''}
        </Tag>
        <Typography.Text type="secondary">{entry.durationMs ?? 0} ms</Typography.Text>
        <Typography.Text type="secondary">{formatBytes(entry.responseSizeBytes)}</Typography.Text>
        {requestId && (
          <Button
            size="small"
            icon={<SaveOutlined />}
            onClick={() => setSavingExample(true)}
            className="ml-auto"
          >
            Save as Example
          </Button>
        )}
      </div>

      <NamePromptModal
        open={savingExample}
        title="Save response as example"
        label="Example name"
        confirmLoading={createExample.isPending}
        onCancel={() => setSavingExample(false)}
        onSubmit={(name) =>
          createExample.mutate(
            {
              name,
              statusCode: entry.statusCode ?? 0,
              statusText: entry.response?.statusText ?? null,
              responseHeaders: entry.response?.headers ?? null,
              responseBody: entry.response?.body ?? null,
            },
            {
              onSuccess: () => {
                void message.success('Saved as example');
                setSavingExample(false);
              },
              onError: (error) => void message.error(error.message || 'Failed to save example'),
            },
          )
        }
      />

      <Tabs
        activeKey={tab}
        onChange={setTab}
        size="small"
        className="min-h-0 flex-1 [&_.ant-tabs-body]:h-full [&_.ant-tabs-body-holder]:h-full [&_.ant-tabs-content]:h-full [&_.ant-tabs-tabpane]:h-full"
        items={[
          {
            key: 'pretty',
            label: 'Pretty',
            children: (
              <div className="h-full overflow-hidden">
                <Editor
                  height="100%"
                  language={language}
                  theme={themeMode === 'dark' ? 'vs-dark' : 'light'}
                  value={prettyBody}
                  options={{ readOnly: true, minimap: { enabled: false }, fontSize: 13 }}
                />
              </div>
            ),
          },
          {
            key: 'raw',
            label: 'Raw',
            children: (
              <pre className="h-full overflow-auto whitespace-pre-wrap break-all p-3 text-xs">
                {entry.response?.body ?? ''}
              </pre>
            ),
          },
          {
            key: 'headers',
            label: `Headers (${headerRows.length})`,
            children: (
              <Table
                size="small"
                pagination={false}
                dataSource={headerRows}
                columns={[
                  { title: 'Name', dataIndex: 'name', width: 240 },
                  { title: 'Value', dataIndex: 'value' },
                ]}
              />
            ),
          },
          {
            key: 'cookies',
            label: `Cookies (${cookieRows.length})`,
            children: (
              <Table
                size="small"
                pagination={false}
                dataSource={cookieRows}
                columns={[
                  { title: 'Name', dataIndex: 'name' },
                  { title: 'Value', dataIndex: 'value' },
                  { title: 'Domain', dataIndex: 'domain' },
                  { title: 'Path', dataIndex: 'path' },
                ]}
              />
            ),
          },
          ...(tabKey
            ? [
                {
                  key: 'tests',
                  label: (() => {
                    const total = testState?.tests.length ?? 0;
                    const failed = testState?.tests.filter((t) => !t.passed).length ?? 0;
                    return total === 0 ? 'Tests' : `Tests (${total - failed}/${total})`;
                  })(),
                  children: (
                    <div className="h-full overflow-auto p-2">
                      {testState?.scriptError && (
                        <Alert
                          className="mb-2"
                          type="error"
                          showIcon
                          title="Test script error"
                          description={testState.scriptError}
                        />
                      )}
                      {(!testState || testState.tests.length === 0) && !testState?.scriptError && (
                        <Empty description="No tests ran for this response" />
                      )}
                      <div className="flex flex-col gap-1">
                        {testState?.tests.map((result, index) => (
                          <div
                            key={`${result.name}-${index}`}
                            className="flex items-start gap-2 rounded border border-black/5 px-2 py-1.5 text-sm dark:border-white/10"
                          >
                            {result.passed ? (
                              <CheckCircleFilled className="mt-0.5 shrink-0 text-emerald-500" />
                            ) : (
                              <CloseCircleFilled className="mt-0.5 shrink-0 text-red-500" />
                            )}
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center justify-between gap-2">
                                <span className="truncate">{result.name}</span>
                                <Typography.Text type="secondary" className="shrink-0 text-xs">
                                  {result.durationMs.toFixed(1)} ms
                                </Typography.Text>
                              </div>
                              {result.error && (
                                <Typography.Text type="danger" className="text-xs">
                                  {result.error}
                                </Typography.Text>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ),
                },
              ]
            : []),
        ]}
      />
    </div>
  );
};
